import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { DomainHit, RunMetadata, FilterState } from '@/lib/trustHub/v1/types';

interface HistoryRunV1 {
  id: string;
  queries: string[];
  providers: string[];
  country: string;
  domains: any[];
  metadata: RunMetadata;
  filters: FilterState;
  created_at: string;
}

interface RunHistoryV1Props {
  onLoadRun: (run: HistoryRunV1) => void;
}

export const RunHistoryV1: React.FC<RunHistoryV1Props> = ({ onLoadRun }) => {
  const [runs, setRuns] = useState<HistoryRunV1[]>([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('trust_hub_runs_v1')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setRuns((data || []) as unknown as HistoryRunV1[]);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load run history');
    } finally {
      setLoading(false);
    }
  };

  const deleteRun = async (id: string) => {
    try {
      const { error } = await supabase
        .from('trust_hub_runs_v1')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setRuns(runs.filter(r => r.id !== id));
      toast.success('Run deleted');
    } catch (error) {
      console.error('Error deleting run:', error);
      toast.error('Failed to delete run');
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Loading history...</p>
      </Card>
    );
  }

  if (runs.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">No saved snapshots yet. Enable "Save Snapshot" when running analyses.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Clock className="h-5 w-5" />
        Saved Snapshots
      </h3>
      <div className="space-y-3">
        {runs.map((run) => (
          <div
            key={run.id}
            className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">
                  {run.providers.join(', ')}
                </span>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground">
                  {run.country !== 'auto' ? run.country : 'Auto'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {run.queries.length} {run.queries.length === 1 ? 'query' : 'queries'} • {' '}
                {run.domains.length} domains found
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onLoadRun(run)}
              >
                <Eye className="h-4 w-4 mr-1" />
                Load
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => deleteRun(run.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};